/**
 * Step 2 · Compatibility & Attributes.
 *
 * Organized into 5 clearly-labeled subsections that mirror the Discover
 * filter drawer and the compat-scoring pillars (Feb 2026 restructure):
 *   1. Compatibility — 8 questions (love language, conflict, communication
 *      communication, family plans, social energy, etc.) — the source of
 *      truth for the compat score.
 *   2. Relationship — goal, kids, marital, monogamy, willing to relocate.
 *   3. Lifestyle    — drinking, smoking, cannabis, exercise, pets.
 *   4. Dating       — open to meeting this week + preferred date types.
 *   (Background = height/education/industry/religion/politics/ethnicity/
 *   languages lives in Step 1 · About You.)
 *
 * Storage:
 *   - Compat questions      → `profile.answers.<key>`
 *   - Relationship & Lifestyle pills → `profile.lifestyle_answers.<key>`
 *   - Dating pills          → `profile.meeting_prefs.<key>`
 *   - Relationship goal     → `profile.looking_for` (canonical) AND
 *                             `profile.lifestyle_answers.relationship_goals`
 */
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ArrowRight } from 'lucide-react';
import { LifestylePill, LIFESTYLE_SECTIONS } from './shared';

// Relationship-goal options — must match backend allowlist
// (routes/profile.py::_LIFESTYLE_ALLOWED_VALUES.relationship_goals) and the
// Discover drawer options.
// Feb 2026 · v2.3.0 — 4 → 5 options, labels reworded to feel less like
// taxonomy and more like a self-identifier. `long_term_open` is a new
// bridging option so users seeking LTR-but-open-to-short-term don't have
// to pick pure `long_term` and get incorrectly clustered.
const RELATIONSHIP_GOAL_OPTIONS = [
  { value: 'life_partner',    label: 'Life Partner' },
  { value: 'long_term',       label: 'Long-Term Relationship' },
  { value: 'long_term_open',  label: 'Relationship, Open to Short-Term' },
  { value: 'casual',          label: 'Casual Dating' },
  { value: 'figuring_out',    label: 'Not Sure Yet' },
];

// Feb 2026 · Phase 135 — CURRENT relationship status. Distinct from
// marital_history (which asks whether someone has ever been married).
// The two questions live near each other on the Relationship section but
// answer different needs: this one sets expectations, that one lets
// filters/scoring reason about lasting matching signals.
const MARITAL_STATUS_OPTIONS = [
  { value: 'single',          label: 'Single' },
  { value: 'separated',       label: 'Separated' },
  { value: 'divorced',        label: 'Divorced' },
  { value: 'widowed',         label: 'Widowed' },
  { value: 'its_complicated', label: "It's complicated" },
];

// Dating section — served by /api/meeting-prefs/options; hard-coded here to
// avoid a second network round-trip inside the setup wizard.
// Feb 2026 · question labels + option copy refreshed for the "How do you
// actually like dating to happen?" framing. Legacy option values retained
// (drinks/food/activities/walks) so existing profile data stays valid;
// newer values (coffee/dinner/outdoors/cultural) are additive.
const MEETING_READINESS_OPTIONS = [
  { value: 'yes',     label: 'This week' },
  { value: 'maybe',   label: 'In a few weeks' },
  { value: 'not_now', label: 'Still exploring' },
];

const DATE_TYPE_OPTIONS = [
  { value: 'coffee',     label: 'Coffee' },
  { value: 'drinks',     label: 'Drinks' },
  { value: 'food',       label: 'Dinner' },        // legacy value → new label
  { value: 'walks',      label: 'Walk' },          // legacy value → new label
  { value: 'activities', label: 'Activity' },      // legacy value → new label
  { value: 'outdoors',   label: 'Outdoors' },
  { value: 'cultural',   label: 'Cultural' },
];

// Feb 2026 · NEW — date-planning style. Two people can love the same date
// type but mismatch on who plans. Stored on `profile.meeting_prefs.date_planning`.
const DATE_PLANNING_OPTIONS = [
  { value: 'i_plan',      label: 'I like to plan' },
  { value: 'they_plan',   label: 'I like them to plan' },
  { value: 'together',    label: 'Plan it together' },
  { value: 'spontaneous', label: "I'm spontaneous" },
];


// --- Small helper for grouped section headers ------------------------------
function SectionHeader({ title, subtitle, testId }) {
  return (
    <div className="pt-1" data-testid={testId}>
      <h3 className="text-[16px] font-bold tracking-tight text-[#0F0F10]">{title}</h3>
      {subtitle && <p className="text-[12px] text-[#8A8A93] mt-0.5">{subtitle}</p>}
    </div>
  );
}


// --- Simple single-select pill row for Relationship goal + Dating fields ---
function PillChoice({ label, subtitle, value, onChange, options, testId }) {
  return (
    <div className="p-3.5 rounded-2xl border border-[var(--parity-hairline)] bg-white" data-testid={testId}>
      <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--parity-ink-soft)] mb-1">
        {label}
      </div>
      {subtitle && (
        <p className="text-[11px] text-[#8A8A93] leading-snug mb-2">{subtitle}</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => onChange(active ? '' : opt.value)}
              data-testid={`${testId}-${opt.value}`}
              className={
                'px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ' +
                (active
                  ? 'bg-[var(--parity-ink)] text-white border-[var(--parity-ink)]'
                  : 'bg-white text-[var(--parity-ink)] border-[var(--parity-hairline)] hover:bg-black/[0.02]')
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// --- Multi-select pill row for date_type_prefs -----------------------------
function PillMulti({ label, values, onToggle, options, testId }) {
  const set = new Set(values || []);
  return (
    <div className="p-3.5 rounded-2xl border border-[var(--parity-hairline)] bg-white" data-testid={testId}>
      <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--parity-ink-soft)] mb-2">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = set.has(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => onToggle(opt.value)}
              data-testid={`${testId}-${opt.value}`}
              className={
                'px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ' +
                (active
                  ? 'bg-[var(--parity-ink)] text-white border-[var(--parity-ink)]'
                  : 'bg-white text-[var(--parity-ink)] border-[var(--parity-hairline)] hover:bg-black/[0.02]')
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export default function StepCompatibility({ profile, setProfile, questions, onNext, onSubmit, submitting, isLastStep }) {
  const answered = Object.keys(profile.answers || {}).length;

  // Relationship goal — canonical write to BOTH `answers.relationship_goals`
  // (where the compat scorer's fallback reads) AND
  // `lifestyle_answers.relationship_goals` (where the wizard historically
  // wrote). Feb 2026 · v2.3.0 — stopped writing to `looking_for`; that
  // field was repurposed as the gender-preference mirror by the
  // Interested-in picker (Phase 129) and the two writes were racing.
  const currentGoal =
    profile.lifestyle_answers?.relationship_goals ||
    profile.answers?.relationship_goals ||
    profile.meeting_prefs?.intent ||
    '';
  const setGoal = (v) =>
    setProfile((p) => ({
      ...p,
      lifestyle_answers: { ...(p.lifestyle_answers || {}), relationship_goals: v },
      answers: { ...(p.answers || {}), relationship_goals: v },
    }));

  // meeting_prefs helpers
  const mp = profile.meeting_prefs || {};
  const setMP = (patch) =>
    setProfile((p) => ({ ...p, meeting_prefs: { ...(p.meeting_prefs || {}), ...patch } }));
  const toggleDateType = (v) => {
    const cur = Array.isArray(mp.date_type_prefs) ? mp.date_type_prefs : [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    setMP({ date_type_prefs: next });
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1">Compatibility</h2>
        <p className="text-zinc-500 text-sm">
          All 10 questions = <span className="text-[#EF4444] font-semibold">Intentional Dater</span> badge ({answered}/10)
        </p>
      </div>

      {/* ============ 1. Compatibility (8 questions) ============ */}
      <SectionHeader
        title="Compatibility"
        subtitle="How you connect, communicate, and build a relationship."
        testId="section-compatibility"
      />
      {questions.map((q, qi) => (
        <div key={q.key} className="bg-[#F8F9FA] border border-black/[0.06] rounded-2xl p-4" data-testid={`question-${q.key}`}>
          <p className="font-medium text-sm mb-3">
            <span className="brand-gradient-text font-bold mr-2">{qi + 1}</span>{q.question}
          </p>
          <RadioGroup
            value={profile.answers?.[q.key] || ''}
            onValueChange={(v) => setProfile((p) => ({ ...p, answers: { ...p.answers, [q.key]: v } }))}
            className="space-y-1"
          >
            {q.options.map((o) => (
              <div key={o.value} className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 cursor-pointer transition-all border ${profile.answers?.[q.key] === o.value ? 'border-[#EF4444]/30 bg-[#EF4444]/8' : 'border-transparent hover:bg-black/[0.02]'}`}>
                <RadioGroupItem value={o.value} id={`${q.key}-${o.value}`} data-testid={`answer-${q.key}-${o.value}`}
                  className="border-zinc-600 text-[#EF4444]" />
                <Label htmlFor={`${q.key}-${o.value}`} className="text-sm text-zinc-700 cursor-pointer flex-1">{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      ))}

      {/* ============ 2. Relationship ============ */}
      <SectionHeader
        title="Relationship"
        subtitle="What you're looking for + family plans."
        testId="section-relationship"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="relationship-fields">
        <PillChoice
          label="Your current status"
          subtitle="Where you are right now. Helps everyone know what to expect."
          value={profile.lifestyle_answers?.marital_status || ''}
          onChange={(v) => setProfile((p) => ({
            ...p,
            lifestyle_answers: { ...(p.lifestyle_answers || {}), marital_status: v },
          }))}
          options={MARITAL_STATUS_OPTIONS}
          testId="setup-marital-status"
        />
        <PillChoice
          label="Relationship goal"
          subtitle="Your relationship goals help us find people looking for the same thing."
          value={currentGoal}
          onChange={setGoal}
          options={RELATIONSHIP_GOAL_OPTIONS}
          testId="setup-relationship-goal"
        />
        {LIFESTYLE_SECTIONS.find((s) => s.title === 'Relationship').fields.map((f) => (
          <LifestylePill
            key={f.key}
            field={f}
            value={profile.lifestyle_answers?.[f.key] || ''}
            onChange={(v) => setProfile((p) => ({
              ...p,
              lifestyle_answers: { ...(p.lifestyle_answers || {}), [f.key]: v },
            }))}
          />
        ))}
      </div>

      {/* ============ 3. Lifestyle ============ */}
      <SectionHeader
        title="Lifestyle"
        subtitle="Drinking, smoking, exercise — small details that matter."
        testId="section-lifestyle"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lifestyle-fields">
        {LIFESTYLE_SECTIONS.find((s) => s.title === 'Lifestyle').fields.map((f) => (
          <LifestylePill
            key={f.key}
            field={f}
            value={profile.lifestyle_answers?.[f.key] || ''}
            onChange={(v) => setProfile((p) => ({
              ...p,
              lifestyle_answers: { ...(p.lifestyle_answers || {}), [f.key]: v },
            }))}
          />
        ))}
      </div>

      {/* ============ 4. Dating ============ */}
      <SectionHeader
        title="Dating"
        subtitle="Change these any time — they help us surface people ready to meet."
        testId="section-dating"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dating-fields">
        <PillChoice
          label="How soon are you open to meeting?"
          value={mp.meeting_readiness || ''}
          onChange={(v) => setMP({ meeting_readiness: v })}
          options={MEETING_READINESS_OPTIONS}
          testId="setup-meeting-readiness"
        />
        <PillChoice
          label="How do you like dates to happen?"
          value={mp.date_planning || ''}
          onChange={(v) => setMP({ date_planning: v })}
          options={DATE_PLANNING_OPTIONS}
          testId="setup-date-planning"
        />
        <PillMulti
          label="Your ideal date (pick a few)"
          values={mp.date_type_prefs || []}
          onToggle={toggleDateType}
          options={DATE_TYPE_OPTIONS}
          testId="setup-date-type-prefs"
        />
      </div>

      <button onClick={isLastStep ? onSubmit : onNext} data-testid="next-step-2" disabled={submitting}
        className="w-full brand-gradient text-white py-3.5 rounded-xl font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.2)] active:scale-[0.98] disabled:opacity-50">
        {submitting ? 'Saving…' : isLastStep ? 'Complete Profile' : 'Continue'}
        <ArrowRight className="inline ml-2 w-4 h-4" />
      </button>
    </div>
  );
}
